import {
  AddressBook,
  Archive,
  BellRinging,
  CalendarBlank,
  Kanban,
  ListChecks,
  NotePencil,
  SlidersHorizontal,
  SunHorizon,
  TrashSimple,
  type Icon,
} from '@phosphor-icons/react';

export type RoutePath =
  | '/today'
  | '/tasks'
  | '/board'
  | '/calendar'
  | '/notes'
  | '/contacts'
  | '/reminders'
  | '/backup'
  | '/trash'
  | '/settings';

export interface RouteDefinition {
  path: RoutePath;
  /** 導覽列與頂部工具列共用的頁面標題。 */
  title: string;
  /** 頁面用途，供導覽 tooltip 與空狀態說明使用。 */
  purpose: string;
  icon: Icon;
  /** 看板需要自己的橫向畫布，不套一般內容最大寬度（spec 7.2）。 */
  fullBleed: boolean;
}

export const ROUTES: readonly RouteDefinition[] = [
  {
    path: '/today',
    title: '今日桌面',
    purpose: '顯示需要關注的工作與行程',
    icon: SunHorizon,
    fullBleed: false,
  },
  {
    path: '/tasks',
    title: '任務清單',
    purpose: '搜尋、篩選、編輯及管理完整歷史',
    icon: ListChecks,
    fullBleed: false,
  },
  {
    path: '/board',
    title: '工作看板',
    purpose: '依自訂欄位安排工作，另提供四象限模式',
    icon: Kanban,
    fullBleed: true,
  },
  {
    path: '/calendar',
    title: '行事曆',
    purpose: '月曆、週曆及行程表',
    icon: CalendarBlank,
    fullBleed: false,
  },
  {
    path: '/notes',
    title: '隨手筆記',
    purpose: '想到什麼就打，不需要存檔',
    icon: NotePencil,
    // 左側清單與右側編輯區都要撐滿高度，不套一般內容最大寬度。
    fullBleed: true,
  },
  {
    path: '/contacts',
    title: '通訊錄',
    purpose: '管理聯絡人及分類',
    icon: AddressBook,
    fullBleed: false,
  },
  {
    path: '/reminders',
    title: '提醒中心',
    purpose: '管理到期、未讀及稍後提醒',
    icon: BellRinging,
    fullBleed: false,
  },
  {
    path: '/backup',
    title: '備份與還原',
    purpose: '匯出、匯入、容量資訊及圖片管理',
    icon: Archive,
    fullBleed: false,
  },
  {
    path: '/trash',
    title: '垃圾桶',
    purpose: '還原或永久刪除資料',
    icon: TrashSimple,
    fullBleed: false,
  },
  {
    path: '/settings',
    title: '設定',
    purpose: '主題、看板、分類、通知及等待閾值等偏好',
    icon: SlidersHorizontal,
    fullBleed: false,
  },
];

export const DEFAULT_ROUTE: RoutePath = '/today';

export interface NavSection {
  id: string;
  label: string;
  items: readonly RouteDefinition[];
}

function pick(...paths: RoutePath[]): RouteDefinition[] {
  return paths.map((path) => {
    const route = ROUTES.find((candidate) => candidate.path === path);
    if (!route) throw new Error(`未定義的路由：${path}`);
    return route;
  });
}

export const NAV_SECTIONS: readonly NavSection[] = [
  { id: 'work', label: '工作', items: pick('/today', '/tasks', '/board', '/calendar', '/notes') },
  { id: 'people', label: '聯絡與提醒', items: pick('/contacts', '/reminders') },
  { id: 'system', label: '資料與設定', items: pick('/backup', '/trash', '/settings') },
];

export function findRouteByPath(path: string): RouteDefinition | undefined {
  return ROUTES.find((route) => route.path === path);
}
